import { ButtonGroup, PrimaryButton, SecondaryButton } from '@ardoq/button';
import { colors, radius, space } from '@ardoq/design-tokens';
import { t, type I18nLocale } from '@ardoq/i18n';
import { FlexBox, Stack } from '@ardoq/layout';
import { Action, PropsReturningActions } from '@ardoq/rxbeach';
import { EmptyState, EmptyStateNoResultIllustration } from '@ardoq/status-ui';
import { Header2, Header3, Text } from '@ardoq/typography';
import React from 'react';
import styled from 'styled-components';

export const ContentWrapperStack = styled(FlexBox).attrs({
  flexDirection: 'column',
  gap: 'xlarge',
})`
  padding: ${space.xlarge};
`;

const Section = styled.section<SectionCardProps>`
  border-radius: ${radius.medium};
  border: 1px solid ${colors.borderSubtle};
  height: ${props => props.$height || 'auto'};
  background-color: ${colors.bgDefault};
`;

type SectionCardProps = {
  $height?: string;
};

export const SectionCard = ({
  children,
  ...props
}: React.PropsWithChildren<SectionCardProps>) => (
  <Section {...props}>{children}</Section>
);

export const SectionWrapper = (props: React.PropsWithChildren) => (
  <Stack gap="small">{props.children}</Stack>
);

export const SectionCardBody = (props: React.PropsWithChildren) => (
  <FlexBox flexDirection="column" gap="medium" padding="large" height="full">
    {props.children}
  </FlexBox>
);

export const SectionCardFooter = (props: React.PropsWithChildren) => (
  <FlexBox
    justify="flex-end"
    padding="medium"
    style={{ borderTop: `1px solid ${colors.borderSubtle}` }}
  >
    {props.children}
  </FlexBox>
);

type ButtonProps = { onClick: VoidFunction; isDisabled: boolean };

export type SaveAndRevertButtonsProps = {
  revertButton: ButtonProps;
  saveButton: ButtonProps;
  i18nLocale: I18nLocale;
};

export const SaveAndRevertButtons = (props: SaveAndRevertButtonsProps) => (
  <ButtonGroup>
    <SecondaryButton
      {...props.revertButton}
      data-tooltip-text={
        props.revertButton.isDisabled
          ? t(
              props.i18nLocale,
              'workspaceManager:noChangesToCancel',
              'No changes to cancel'
            )
          : undefined
      }
    >
      {t(props.i18nLocale, 'workspaceManager:cancel', 'Cancel')}
    </SecondaryButton>
    <PrimaryButton
      {...props.saveButton}
      data-tooltip-text={
        props.saveButton.isDisabled
          ? t(
              props.i18nLocale,
              'workspaceManager:noChangesToSave',
              'No changes to save'
            )
          : undefined
      }
    >
      {t(props.i18nLocale, 'workspaceManager:save', 'Save')}
    </PrimaryButton>
  </ButtonGroup>
);

export const toSaveAndRevertButtonsProps = (props: {
  commands: {
    onSave: () => Action<unknown>;
    onRevert: () => Action<unknown>;
  };
  hasChanges: boolean;
  i18nLocale: I18nLocale;
}): PropsReturningActions<SaveAndRevertButtonsProps> => ({
  revertButton: {
    onClick: props.commands.onRevert,
    isDisabled: !props.hasChanges,
  },
  saveButton: {
    onClick: props.commands.onSave,
    isDisabled: !props.hasChanges,
  },
  i18nLocale: props.i18nLocale,
});

export const PageTitleWrapper = (props: React.PropsWithChildren) => (
  <FlexBox
    flexDirection="column"
    paddingBottom="small"
    gap="xsmall"
    style={{ borderBottom: `1px solid  ${colors.borderSubtle} ` }}
  >
    {props.children}
  </FlexBox>
);

export const PageTitle = (props: { title: string }) => (
  <Header2>{props.title}</Header2>
);

export const PageSubtitle = (props: React.PropsWithChildren) => (
  <Text variant="caption" color="textSubtle">
    {props.children}
  </Text>
);

export const PageSecondaryTitle = (props: { title: string }) => (
  <Header3>{props.title}</Header3>
);

export const MaxWidthWrapper = (props: React.PropsWithChildren) => (
  <FlexBox flexDirection="column" width="full" maxWidth="800px">
    {props.children}
  </FlexBox>
);

type WorkspaceManagerEmptyStateProps = {
  title: string;
  description: string;
};

export const WorkspaceManagerEmptyState = (
  props: WorkspaceManagerEmptyStateProps
) => (
  <FlexBox
    borderWidth={1}
    borderRadius="medium"
    align="center"
    justify="center"
    paddingY="large"
  >
    <EmptyState
      title={props.title}
      description={props.description}
      media={<EmptyStateNoResultIllustration />}
    />
  </FlexBox>
);
